import {
  FormEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";
import { CAKey, Scheme, SchemeInfo, allSchemes } from "../utils/helpers/types";
import Table, { Column } from "../components/table/Table";
import LayoutContext from "../context/layout-context";
import {
  activateCAKey,
  createCAKey,
  createScheme,
  deactivateCAKey,
  deprecateCAKey,
  getAllSchemes,
  getCAKeysByScheme,
  undeprecateCAKey,
  updateScheme
} from "../utils/helpers/data/certificate-authority";
import ContextMenu, {
  ContextOption
} from "../components/context-menu/ContextMenu";
import Switch from "../components/switch/Switch";
import Button from "../components/button/Button";
import Modal from "../components/modal/Modal";
import Select, { Option } from "../components/select/Select";
import FileUploader from "../components/file-uploader/FileUploader";
import Input from "../components/input/Input";

const CertificateAuthority: FunctionComponent = () => {
  const [dataSource, setDataSource] = useState<SchemeInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [showSchemeModal, setShowSchemeModal] = useState(false);
  const [schemeOnEdit, setSchemeOnEdit] = useState<SchemeInfo | null>(null);

  const displayAddEditModal = () => {
    setShowAddEditModal(true);
  };

  const dismissAddEditModal = () => {
    setShowAddEditModal(false);
  };

  const displaySchemeModal = (scheme: SchemeInfo | null) => {
    setSchemeOnEdit(scheme);
    setShowSchemeModal(true);
  };

  const dismissSchemeModal = () => {
    setSchemeOnEdit(null);
    setShowSchemeModal(false);
  };

  const { notify } = useContext(LayoutContext);

  const fetchSchemes = async () => {
    setLoading(true);
    const { error, message, data } = await getAllSchemes();
    setLoading(false);
    if (error || !data) {
      notify("error", `Unable to fetch schemes: ${message}`);
      return;
    }
    setDataSource(data);
  };

  const columns: Column<SchemeInfo>[] = [
    {
      title: "Scheme Code",
      dataIndex: "code",
      key: "code"
    },
    {
      title: "Scheme Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description"
    },
    {
      title: "Created On",
      dataIndex: "createdOn",
      key: "createdOn"
    },
    {
      title: "",
      dataIndex: "id",
      key: "id",
      render: (_, scheme) => (
        <Button
          type="transparent"
          size="small"
          onClick={e => {
            e.stopPropagation();
            displaySchemeModal(scheme);
          }}
        >
          <strong className="primary">Edit</strong>
        </Button>
      )
    }
  ];

  useEffect(() => {
    fetchSchemes();
  }, []);

  return (
    <main>
      <h1 className="title vertical-margin spaced thin">
        Certificate Authority Keys
      </h1>
      <div className="flex end vertical-margin spaced center-align">
        <div className="flex spaced">
          <Button onClick={() => displaySchemeModal(null)}>
            New Card Scheme
          </Button>
          <Button onClick={() => displayAddEditModal()}>New CA Key</Button>
        </div>
      </div>

      <Table
        dataSource={dataSource}
        columns={columns}
        loading={loading}
        rowExpandRender={(row, isMounted) =>
          isMounted && <CAKeysRender scheme={row} />
        }
      />

      <AddEditModal
        visible={showAddEditModal}
        cancel={() => dismissAddEditModal()}
        onComplete={() => {
          dismissAddEditModal();
          fetchSchemes();
        }}
      />

      <AddSchemeModal
        visible={showSchemeModal}
        cancel={dismissSchemeModal}
        scheme={schemeOnEdit}
        onComplete={() => {
          dismissSchemeModal();
          fetchSchemes();
        }}
      />
    </main>
  );
};

const schemeOptions: Option[] = allSchemes.map(scheme => ({
  label: scheme
    .toLowerCase()
    .replace(/^./, char => char.toUpperCase())
    .replace(/_/, " "),
  value: scheme
}));

interface AddEditProps {
  visible: boolean;
  cancel: () => void;
  onComplete: (stayOnPage: boolean) => void;
}

const AddEditModal: FunctionComponent<AddEditProps> = ({
  visible,
  cancel,
  onComplete
}) => {
  const [loading, setLoading] = useState(false);
  const [scheme, setScheme] = useState<Scheme | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [modulusLength, setModulusLength] = useState("");

  const { notify } = useContext(LayoutContext);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error, message } = await createCAKey({
      scheme: scheme as Scheme,
      file: file as File,
      modulusLength
    });
    setLoading(false);
    if (error) {
      notify("error", `Unable to import CA key: ${message}`);
    } else {
      notify("success", `CA key imported successfully`);
      onComplete(false);
      cancel();
    }
  };

  return (
    <Modal visible={visible} cancel={cancel} size="small">
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <span className="question">Issuer name</span>
          <Select
            value={scheme || ""}
            options={schemeOptions}
            placeholder="Select Scheme"
            onSelect={value => setScheme(value as Scheme)}
            responsive
          />
        </div>

        <div className="input-group">
          <span className="question">Upload CA key file</span>
          <FileUploader
            acceptedTypes={[]}
            titleText="Upload CA key file"
            subtitle="Or drag CA key file to upload"
            file={file}
            onChange={setFile}
          />
        </div>

        <div className="input-group">
          <span className="question">Modulus Length</span>
          <Input
            value={modulusLength}
            onChange={setModulusLength}
            placeholder="Enter modulus length (in bits)"
            inputValidation={{
              required: true,
              transform: val => val.toUpperCase().replace(/\D/g, "")
            }}
            responsive
          />
        </div>

        <div className="vertical-margin flex end spaced">
          <Button type="accent" onClick={cancel} size="small">
            Cancel
          </Button>
          <Button loading={loading} buttonType="submit" size="small">
            Import new CA key
          </Button>
        </div>
      </form>
    </Modal>
  );
};

interface AddSchemeProps {
  visible: boolean;
  cancel: () => void;
  scheme: SchemeInfo | null;
  onComplete: () => void;
}

const AddSchemeModal: FunctionComponent<AddSchemeProps> = ({
  visible,
  cancel,
  scheme,
  onComplete
}) => {
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const isEdit = Boolean(scheme);

  const { notify } = useContext(LayoutContext);

  useEffect(() => {
    if (scheme) {
      setCode(scheme.code || "");
      setName(scheme.name || "");
      setDescription(scheme.description || "");
    } else {
      setCode("");
      setName("");
      setDescription("");
    }
  }, [scheme]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error, message } = isEdit
      ? await updateScheme({ id: scheme!.id, code, name, description })
      : await createScheme({ code, name, description });
    setLoading(false);
    if (error) {
      notify("error", `Unable to ${isEdit ? "update" : "create"} scheme: ${message}`);
    } else {
      notify("success", `Scheme ${isEdit ? "updated" : "created"} successfully`);
      setCode("");
      setName("");
      setDescription("");
      onComplete();
    }
  };

  const handleCancel = () => {
    setCode("");
    setName("");
    setDescription("");
    cancel();
  };

  return (
    <Modal visible={visible} cancel={handleCancel} size="small">
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <span className="question">Scheme Code</span>
          <Input
            value={code}
            onChange={setCode}
            placeholder="Enter scheme code"
            inputValidation={{
              required: true,
              transform: val => val.toUpperCase()
            }}
            responsive
            disabled={isEdit}
          />
        </div>

        <div className="input-group">
          <span className="question">Scheme Name</span>
          <Input
            value={name}
            onChange={setName}
            placeholder="Enter scheme name"
            inputValidation={{
              required: true
            }}
            responsive
          />
        </div>

        <div className="input-group">
          <span className="question">Description</span>
          <Input
            value={description}
            onChange={setDescription}
            placeholder="Enter description (optional)"
            responsive
          />
        </div>

        <div className="vertical-margin flex end spaced">
          <Button type="accent" onClick={handleCancel} size="small">
            Cancel
          </Button>
          <Button loading={loading} buttonType="submit" size="small">
            {isEdit ? "Update Card Scheme" : "Create Card Scheme"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

interface CAKeysRenderProps {
  scheme: SchemeInfo;
}

const CAKeysRender: FunctionComponent<CAKeysRenderProps> = ({ scheme }) => {
  const [loading, setLoading] = useState(false);
  const [caKeys, setCAKeys] = useState<CAKey[]>([]);
  const [showDeprecated, setShowDeprecated] = useState(false);

  const { notify, confirm } = useContext(LayoutContext);

  const fetchCAKeys = async (deprecated?: boolean) => {
    if (!scheme.code) {
      return;
    }
    setLoading(true);
    const deprecatedFilter =
      deprecated !== undefined ? deprecated : showDeprecated;
    const { error, message, data } = await getCAKeysByScheme(
      scheme.code,
      deprecatedFilter
    );
    setLoading(false);
    if (error || !data) {
      notify("error", `Unable to fetch CA keys: ${message}`);
      return;
    }
    setCAKeys(data);
  };

  const handleDeprecatedToggle = (checked: boolean) => {
    setShowDeprecated(checked);
  };

  const handleDeprecate = async (key: CAKey) => {
    const { error, message } = await deprecateCAKey(key);
    if (error) {
      notify("error", `Unable to deprecate CA key: ${message}`);
      return;
    }
    notify("success", `CA key ${key.keyLabel} has been deprecated`);
    fetchCAKeys();
  };

  const handleUndeprecate = async (key: CAKey) => {
    const { error, message } = await undeprecateCAKey(key);
    if (error) {
      notify("error", `Unable to undeprecate CA key: ${message}`);
      return;
    }
    notify("success", `CA key ${key.keyLabel} has been undeprecated`);
    fetchCAKeys();
  };

  const handleActivate = async (key: CAKey) => {
    const { error, message } = await activateCAKey(key);
    if (error) {
      notify("error", `Unable to activate CA key: ${message}`);
      return;
    }
    notify("success", `CA key ${key.keyLabel} has been activated`);
    fetchCAKeys();
  };

  const handleDeactivate = async (key: CAKey) => {
    const { error, message } = await deactivateCAKey(key);
    if (error) {
      notify("error", `Unable to deactivate CA key: ${message}`);
      return;
    }
    notify("success", `CA key ${key.keyLabel} has been deactivated`);
    fetchCAKeys();
  };

  useEffect(() => {
    fetchCAKeys();
  }, [scheme.code, showDeprecated]);

  const columns: Column<CAKey>[] = [
    {
      title: "Key Label",
      dataIndex: "keyLabel",
      key: "keyLabel",
      render: (keyLabel: string) => (
        <span
          title={keyLabel}
          style={{
            display: "inline-block",
            maxWidth: "200px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
        >
          {keyLabel}
        </span>
      )
    },
    {
      title: "RID",
      dataIndex: "rid",
      key: "rid"
    },
    {
      title: "Key Index",
      dataIndex: "keyIndex",
      key: "keyIndex"
    },
    {
      title: "Activation Status",
      dataIndex: "activated",
      key: "activated",
      render: (activated: boolean) => (
        <span className={`tag smaller ${activated ? "success" : "error"}`}>
          {activated ? "ACTIVE" : "INACTIVE"}
        </span>
      )
    },
    {
      title: "Deprecated",
      dataIndex: "deprecated",
      key: "deprecated",
      render: (deprecated: boolean) => (
        <span className={`tag smaller ${deprecated ? "success" : "error"}`}>
          {deprecated ? "YES" : "NO"}
        </span>
      )
    },
    {
      title: "",
      dataIndex: "id",
      key: "id",
      render: (_, key) => (
        <ContextMenu
          options={
            [
              !key.activated && {
                label: <strong className="success">Activate</strong>,
                onClick: () =>
                  confirm({
                    title: `Do you want to activate the CA key: ${key.keyLabel}`,
                    body: "",
                    onOk: async () => handleActivate(key)
                  })
              },
              key.activated && {
                label: <strong className="danger">Deactivate</strong>,
                onClick: () =>
                  confirm({
                    title: `Do you want to deactivate the CA key: ${key.keyLabel}`,
                    body: "",
                    onOk: async () => handleDeactivate(key)
                  })
              },
              !key.deprecated && {
                label: <strong className="warning">Deprecate</strong>,
                onClick: () =>
                  confirm({
                    title: `Do you want to deprecate the CA key: ${key.keyLabel}`,
                    body: "This will mark the key as deprecated.",
                    onOk: async () => handleDeprecate(key)
                  })
              },
              key.deprecated && {
                label: <strong className="success">Undeprecate</strong>,
                onClick: () =>
                  confirm({
                    title: `Do you want to undeprecate the CA key: ${key.keyLabel}`,
                    body: "This will restore the key to active status.",
                    onOk: async () => handleUndeprecate(key)
                  })
              }
            ].filter(Boolean) as ContextOption[]
          }
        />
      )
    }
  ];

  return (
    <div style={{ backgroundColor: "rgb(244, 242, 242)", padding: "1rem" }}>
      <div className="flex between vertical-margin spaced center-align">
        <h3 className="thin">
          CA Keys for {scheme.name || scheme.code} ({caKeys.length})
        </h3>
        <Switch
          checked={showDeprecated}
          onChange={handleDeprecatedToggle}
          text="Show Deprecated"
        />
      </div>
      <Table
        dataSource={caKeys}
        columns={columns}
        loading={loading}
        emptyText="No CA keys found for this scheme"
      />
    </div>
  );
};

export default CertificateAuthority;
